/**
 * LA ETIQUETA ÚNICA de un estimado (regla estricta 2026-08-18). El backend la decide UNA
 * vez en listClaimEstimates (tag + round_number) y todas las pantallas la pintan igual:
 * Files, Review Estimate (paneles A/B) y el encabezado del item-by-item ("A vs B").
 *
 *  · proactive_initial → "Proactive initial estimate" (nuestro 1º; con esto el claim no
 *    vuelve a First Estimate)
 *  · proactive         → "Proactive estimate" (los nuestros siguientes)
 *  · repair            → "Repair estimate" (amarilla; NO es aprobación full ni base)
 *  · approved          → "Approved estimate" (el 1er FULL del seguro = la BASE)
 *  · round             → "Round N" (2ª respuesta del seguro en adelante, hasta resolved)
 */
import { useEffect, useState } from 'react';
import { api } from './api';

export type EstTag = 'proactive_initial' | 'proactive' | 'counter' | 'repair' | 'approved' | 'round' | 'mitigation' | 'insurance'
  | 'tarp' | 'ins_mitigation' | 'atr';
/** Las etiquetas del seguro que SUMAN al RCV (dueño 2026-10-23): tarp, mitigación, inspección / ATR. Fuera de la secuencia. */
export const ETIQUETAS_QUE_SUMAN: readonly EstTag[] = ['tarp', 'ins_mitigation', 'atr'];
/** El valor del desplegable para cada una (lo que viaja en `mode` a PUT /estimate-tag). */
export const MODO_DE_ETIQUETA: Record<'tarp' | 'ins_mitigation' | 'atr', 'tarp' | 'mitigation' | 'atr'> = { tarp: 'tarp', ins_mitigation: 'mitigation', atr: 'atr' };

export type EtiquetaVisual = { text: string; bg: string; fg: string };

export function etiquetaEstimado(
  t: (k: string, vars?: Record<string, string>) => string,
  tag?: EstTag | null,
  roundNumber?: number | null,
): EtiquetaVisual | null {
  switch (tag) {
    case 'proactive_initial':
      return { text: t('files.initialOurs'), bg: 'var(--accent-soft)', fg: 'var(--accent)' };
    case 'proactive':
      return { text: t('etag.proactive'), bg: 'var(--surface-2)', fg: 'var(--muted)' };
    case 'counter':
      // El estimado NUESTRO que sale de la comparación N (regla 2026-08-18).
      return { text: t('etag.counter', { n: String(roundNumber ?? 1) }), bg: 'var(--info-soft, #e0f2fe)', fg: 'var(--info, #0369a1)' };
    case 'repair':
      return { text: t('files.repairEstimate'), bg: 'var(--orange-soft, #fef3c7)', fg: 'var(--orange, #b45309)' };
    case 'approved':
      return { text: t('etag.approved'), bg: 'var(--green-soft)', fg: 'var(--green)' };
    case 'round':
      return { text: t('etag.round', { n: String(roundNumber ?? 2) }), bg: 'var(--purple-soft, #f3e8ff)', fg: 'var(--purple, #7c3aed)' };
    case 'mitigation':
      // Fuera de la secuencia: un estimado de mitigación (tarps, agua) no es el inicial ni un counter.
      return { text: t('etag.mitigation'), bg: 'var(--teal-soft, #ccfbf1)', fg: 'var(--teal, #0f766e)' };
    case 'insurance':
      // Del seguro SIN monto legible: no consume número de ronda hasta que alguien se lo ponga.
      return { text: t('etag.insurance'), bg: 'var(--surface-2)', fg: 'var(--muted)' };
    // DEL SEGURO Y SUMAN AL RCV (dueño 2026-10-23): no son ronda ni base; el tarp / la mitigación / la inspección se
    // cobran APARTE de la ronda vigente, y Payments los suma.
    case 'tarp':
      return { text: t('etag.tarp'), bg: 'var(--teal-soft, #ccfbf1)', fg: 'var(--teal, #0f766e)' };
    case 'ins_mitigation':
      return { text: t('etag.insMitigation'), bg: 'var(--teal-soft, #ccfbf1)', fg: 'var(--teal, #0f766e)' };
    case 'atr':
      return { text: t('etag.atr'), bg: 'var(--teal-soft, #ccfbf1)', fg: 'var(--teal, #0f766e)' };
    default:
      return null;
  }
}

/**
 * Resuelve claim_file id → etiqueta única del estimado (listClaimEstimates es la fuente).
 * Vivía en NegotiationPanel; se movió acá cuando Files también empezó a pintar las
 * etiquetas del seguro (reporte Wilcher 2026-08-18: el seguro salía sin etiqueta).
 */
export type LadoEstimado = 'ours' | 'insurance';
/**
 * El resolver es una FUNCIÓN (así lo usan Files y Review Estimate) con dos datos colgados que
 * hacen falta para poder CORREGIR la etiqueta a mano (2026-08-21): de qué lado es el archivo y
 * cuántas versiones tiene ese lado — sin eso el desplegable no sabe si ofrecer "Round N" o
 * "Counter offer N", ni hasta qué número llegar. Van colgados de la misma función en vez de en
 * un segundo hook para no pedir dos veces al servidor la misma respuesta.
 */
export type EstimateTagResolver = ((fileId?: string | null) => (EtiquetaVisual & { tag: EstTag }) | null) & {
  ladoDe: (fileId?: string | null) => LadoEstimado | null;
  versiones: (lado: LadoEstimado) => number;
  /** Lo que hay puesto A MANO en ese archivo: 'auto' | 'repair' | 'excluded' | 'n<N>'. El
   *  desplegable tiene que MOSTRAR lo puesto, no deducirlo del rótulo — un estimado excluido
   *  y uno sin monto legible se ven igual desde afuera y son cosas distintas. */
  manualDe: (fileId?: string | null) => string;
  /** DÓNDE VA ESTE ESTIMADO EN LA SECUENCIA — ver `ordenEnLaSecuencia`. */
  ordenDe: (fileId?: string | null) => number;
};

/**
 * EL ORDEN DE LA NEGOCIACIÓN, como se lee: nuestro inicial → el aprobado del seguro → nuestro
 * counter 1 → Round 2 → counter 2 → Round 3…
 *
 * Pedido del dueño (2026-08-21): "en estimate, que estén todos organizados: primero el insurance
 * approval, después el counter offer, después venga el round two y así, bien organizado".
 *
 * Por qué media unidad y no un contador aparte: la ronda ya es el número que ordena las dos
 * partes. La respuesta del seguro a la ronda N es N; lo que nosotros contestamos DESPUÉS cae
 * entre N y N+1. Con eso los dos lados se intercalan solos y no hay una segunda numeración que
 * mantener sincronizada con la de la ley (que es lo que nos hizo tener "números que no cuadran"
 * en otras pantallas).
 *
 * Lo que NO es una ronda —mitigación, un estimado del seguro sin monto legible, uno excluido a
 * mano— se va al final en vez de meterse en el medio con un número inventado.
 */
export function ordenEnLaSecuencia(tag?: EstTag | null, roundNumber?: number | null): number {
  const n = roundNumber ?? null;
  switch (tag) {
    case 'proactive_initial': return 0;             // el primero que existe: el nuestro
    case 'approved': return 1;                      // la primera respuesta FULL del seguro = la base
    case 'round': return n ?? 2;                    // Round N
    case 'repair': return (n ?? 1) + 0.25;          // pegado a la ronda que contesta
    case 'counter': return (n ?? 1) + 0.5;          // nuestra respuesta a esa ronda
    case 'proactive': return (n ?? 0) + 0.75;       // nuestro, sin número de counter propio
    case 'mitigation': return 900;                  // fuera de la secuencia
    case 'insurance': return 901;                   // del seguro pero sin monto → todavía sin lugar
    case 'tarp': return 902;                        // del seguro, suma al RCV: aparte de las rondas
    case 'ins_mitigation': return 903;
    case 'atr': return 904;
    default: return 9999;                           // sin etiqueta: al final, por fecha
  }
}

export function useEstimateTagResolver(claimId: string, t: (k: string, vars?: Record<string, string>) => string, refreshKey?: unknown): EstimateTagResolver {
  const [tagsDe, setTagsDe] = useState<Record<string, { tag: EstTag; round_number: number | null; lado: LadoEstimado; round_override: number | null; sequence_excluded: boolean }>>({});
  const [versiones, setVersiones] = useState<{ ours: number; insurance: number }>({ ours: 0, insurance: 0 });
  // refreshKey: el llamador lo cambia cuando su lista se recarga (subida/corrección de monto)
  // para que el chip del archivo nuevo no espere a un remount (revisión 2026-08-19).
  useEffect(() => {
    let vivo = true;
    type Op = { id: string; tag: EstTag; round_number: number | null; round_override?: number | null; sequence_excluded?: boolean };
    api.get<{ ours: Op[]; insurance: Op[]; oursVersions?: unknown[]; insuranceVersions?: unknown[] }>(`/api/claims/${claimId}/negotiation/estimates`)
      .then((r) => {
        if (!vivo) return;
        setTagsDe(Object.fromEntries([
          ...r.ours.map((o) => [String(o.id), { tag: o.tag, round_number: o.round_number, lado: 'ours' as const, round_override: o.round_override ?? null, sequence_excluded: Boolean(o.sequence_excluded) }] as const),
          ...r.insurance.map((o) => [String(o.id), { tag: o.tag, round_number: o.round_number, lado: 'insurance' as const, round_override: o.round_override ?? null, sequence_excluded: Boolean(o.sequence_excluded) }] as const),
        ]));
        setVersiones({ ours: r.oursVersions?.length ?? 0, insurance: r.insuranceVersions?.length ?? 0 });
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [claimId, refreshKey]);
  const resolver = (fileId?: string | null): (EtiquetaVisual & { tag: EstTag }) | null => {
    const info = fileId ? tagsDe[String(fileId)] : undefined;
    const v = etiquetaEstimado(t, info?.tag, info?.round_number);
    return v && info ? { ...v, tag: info.tag } : null;
  };
  return Object.assign(resolver, {
    ladoDe: (fileId?: string | null) => (fileId ? tagsDe[String(fileId)]?.lado ?? null : null),
    versiones: (lado: LadoEstimado) => versiones[lado],
    manualDe: (fileId?: string | null) => {
      const info = fileId ? tagsDe[String(fileId)] : undefined;
      if (!info) return 'auto';
      if (info.sequence_excluded) return 'excluded';
      if (info.tag === 'repair') return 'repair';
      if (info.tag === 'tarp' || info.tag === 'ins_mitigation' || info.tag === 'atr') return MODO_DE_ETIQUETA[info.tag];
      if (info.round_override != null) return `n${info.round_override}`;
      return 'auto';
    },
    ordenDe: (fileId?: string | null) => {
      const info = fileId ? tagsDe[String(fileId)] : undefined;
      return ordenEnLaSecuencia(info?.tag, info?.round_number);
    },
  });
}
